<template lang="html">
  <div class="fsd-gmaps">
    <template v-if="consentGoogle === 'GRANTED'">
      <div
        v-click-track="{ action_type: `map_toggle_${visible ? 'on' : 'off'}` }"
        for="maps-toggle"
        class="google-maps__toggle-label"
        @click="toggleMap"
      >
        <span>{{ 'client.fused.job.map' | t }}</span>
        <svg-icon :icon-name="icon" class-name="fsd-gmaps__icon" />
      </div>
      <div v-show="visible" ref="map" class="fsd-gmaps__map" />
    </template>
    <template v-else-if="consentGoogle === 'DENIED'">
      <div class="google-maps__toggle-label" @click="onRequestConsent">
        <span>{{ 'client.fused.consentable.map' | t }}</span>
        <svg-icon :icon-name="icon" class-name="fsd-gmaps__icon" />
      </div>
    </template>
  </div>
</template>

<script>
import GoogleMapsApi from '@/google_maps_api'
import castArray from 'lodash-es/castArray'
import { requestConsent } from '@/util/consent'
import { mapState } from 'vuex'

export default {
  props: {
    label: { type: String, required: true },
    query: { type: String, required: true },
    zoom: { type: Number, default: 14 }
  },
  data: () => {
    return {
      visible: false,
      initialized: false,
      mapLabels: [],
      geocoder: null,
      map: null,
      consentGoogle: null,
      consentRequested: false
    }
  },
  computed: {
    ...mapState(['jobMarket', 'consent']),
    icon() {
      return this.visible ? 'chevron-up' : 'chevron-down'
    },
    locations() {
      return castArray(this.query)
    },
    bounds() {
      const bounds = new google.maps.LatLngBounds()
      this.mapLabels.forEach(mapLabel => {
        bounds.extend(mapLabel.marker.position)
      })
      return bounds
    }
  },
  watch: {
    'consent.google': function (newConsent, oldConsent) {
      // trigger toggleMap() for users who requestedConsent (probably) via onRequestConsent()
      if (
        oldConsent === 'DENIED' &&
        newConsent === 'GRANTED' &&
        this.consentRequested
      ) {
        this.consentRequested = false
        this.toggleMap()
      }
      // reset component if consent gets removed on an initialized map
      if (newConsent === 'DENIED' && this.initialized) {
        this.visible = false
        this.initialized = false
        this.map = null
        this.geocoder = null
        this.mapLabel = []
        this.consentRequested = false
      }
      this.consentGoogle = newConsent
    }
  },
  mounted() {
    this.consentGoogle = this.consent.google
  },
  methods: {
    onRequestConsent() {
      this.consentRequested = true
      requestConsent()
    },
    intialize() {
      return new GoogleMapsApi()
        .load()
        .then(() => {
          this.geocoder = new google.maps.Geocoder()
          this.map = new google.maps.Map(this.$refs.map, { zoom: this.zoom })
          return this.generateMapLabels()
        })
        .then(() => {
          this.formatMap()
        })
    },
    toggleMap() {
      this.visible = !this.visible
      this.$emit('toggled', this.visible)
      // Only intialize and geocode when we actually open the map and only do it once, so we don't cause any unnecssary
      // requests to the Google Geocoding API (which isn't free).
      if (!this.initialized) {
        this.intialize().then(() => {
          this.initialized = true
        })
      }
    },
    generateMapLabels() {
      const locationPromises = this.locations.map(location => {
        return new Promise((resolve, reject) => {
          this.geocoder.geocode({ address: location }, (results, status) => {
            if (status === 'OK') {
              this.addMapLabel(location, results[0].geometry.location)
              resolve()
            } else {
              reject(new Error('geocoding not succesful'))
            }
          })
        })
      })

      return Promise.all(locationPromises)
    },
    markerInfoWindow(location) {
      return new google.maps.InfoWindow({
        content:
          '<p class="google-maps__js-infotext"><b class="google-maps__js-infotext--bold">' +
          this.label +
          '</b><br>' +
          location +
          '</p>'
      })
    },
    marker(latLngPosition) {
      return new google.maps.Marker({
        map: this.map,
        position: latLngPosition
      })
    },
    addMapLabel(location, latLngPosition) {
      const mapLabel = {
        marker: this.marker(latLngPosition),
        info: this.markerInfoWindow(location)
      }
      mapLabel.marker.addListener('click', function () {
        mapLabel.info.open(this.map, mapLabel.marker)
      })
      this.mapLabels.push(mapLabel)
    },
    formatMap() {
      if (this.mapLabels.length > 1) {
        this.map.fitBounds(this.bounds)
        this.map.panToBounds(this.bounds)
        // TODO: broken auto zoom for more than 1 marker
        this.map.setZoom(this.zoom)
      } else {
        this.map.setCenter(this.mapLabels[0].marker.position)
        this.mapLabels[0].info.open(this.map, this.mapLabels[0].marker)
      }
    }
  }
}
</script>

<style lang="css" scoped></style>
